import { useCallback, useEffect, useRef, useState } from 'react';
import logger from '../../../utils/logger';

/**
 * Resolves a list of user ids (e.g. `song.likes`) into an array of user
 * detail objects via `getUserDetailsById`. Fetched in parallel and isolated
 * per id — one user failing to resolve (e.g. a transient Firestore error)
 * no longer stalls the whole list forever, which used to show the likers
 * modal permanently empty.
 *
 * Returns `{ likersDetails, isLoading, hasError, retry }`; `likersDetails`
 * omits ids that failed to resolve, and `hasError` signals that at least
 * one did so callers can show a retry affordance.
 */
const useLikersDetails = (userIds, getUserDetailsById) => {
  const [likersDetails, setLikersDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!userIds || userIds.length === 0) {
      setLikersDetails([]);
      setIsLoading(false);
      setHasError(false);
      return undefined;
    }

    cancelledRef.current = false;
    setIsLoading(true);
    setHasError(false);

    const fetchLikersDetails = async () => {
      const results = await Promise.allSettled(
        userIds.map((userId) => getUserDetailsById(userId))
      );
      if (cancelledRef.current) return;

      const resolved = [];
      let failed = false;
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          resolved.push(result.value);
        } else {
          failed = true;
          logger.error(
            `[useLikersDetails] Failed to load user ${userIds[index]}:`,
            result.reason
          );
        }
      });

      setLikersDetails(resolved);
      setHasError(failed);
      setIsLoading(false);
    };

    fetchLikersDetails();

    return () => {
      cancelledRef.current = true;
    };
  }, [userIds, getUserDetailsById, retryToken]);

  const retry = useCallback(() => setRetryToken((t) => t + 1), []);

  return { likersDetails, isLoading, hasError, retry };
};

export default useLikersDetails;
